import create from 'zustand';
import { resolveAccessTokenForRequest } from '@utils/auth/getClientAccessToken';
import {
  PrimePitchDismissNavigation,
  PrimePitchOpenOptions,
  PrimeTrialPromoContextualId,
  PrimeTrialPromoModalData
} from './types';
import fetchPrimeTrialPromoDetails from './api';
import mapPrimeTrialPromoResponseToModalData from './mapper';

interface PrimeTrialPromoModalStore {
  open: boolean;
  loading: boolean;
  error: string | null;

  contextId: PrimeTrialPromoContextualId | null;
  skuUid: string | null;
  goalUID: string | null;
  goalSlug: string | null;
  requestId: number;
  dismissNavigateTo?: PrimePitchDismissNavigation;
  onDismiss?: () => void;
  onJoinPrime?: () => void;

  data: PrimeTrialPromoModalData | null;
  analyticsContext: PrimePitchOpenOptions['analyticsContext'] | null;
  subscriptionPrice: number | null;

  openModal: (
    contextId: PrimeTrialPromoContextualId,
    goalUID: string,
    goalSlug: string,
    options?: PrimePitchOpenOptions
  ) => Promise<void>;
  closeModal: () => void;
  joinPrime: () => void;
}

export default create<PrimeTrialPromoModalStore>((set, get) => ({
  open: false,
  loading: false,
  error: null,

  contextId: null,
  skuUid: null,
  goalUID: null,
  goalSlug: null,
  requestId: 0,
  dismissNavigateTo: undefined,
  onDismiss: undefined,
  onJoinPrime: undefined,

  data: null,
  analyticsContext: null,
  subscriptionPrice: null,

  openModal: async (
    contextId: PrimeTrialPromoContextualId,
    goalUID: string,
    goalSlug: string,
    options = {}
  ) => {
    const {
      dismissNavigateTo,
      analyticsContext = {},
      onDismiss,
      onJoinPrime
    } = options;
    const nextRequestId = get().requestId + 1;

    set({
      open: true,
      loading: true,
      error: null,
      contextId,
      goalUID,
      goalSlug,
      requestId: nextRequestId,
      dismissNavigateTo,
      onDismiss,
      onJoinPrime,
      analyticsContext
    });

    try {
      const accessToken = await resolveAccessTokenForRequest();

      if (!accessToken) {
        throw new Error('Access token is required');
      }

      const response = await fetchPrimeTrialPromoDetails({
        goalUID,
        accessToken
      });

      if (get().requestId !== nextRequestId) {
        return;
      }

      const subscriptionPrice = Number(response.ctaDetails.price);
      set({
        data: mapPrimeTrialPromoResponseToModalData(response),
        skuUid: response.ctaDetails.skuUid,
        subscriptionPrice: Number.isNaN(subscriptionPrice)
          ? null
          : subscriptionPrice,
        loading: false
      });
    } catch (error) {
      if (get().requestId !== nextRequestId) {
        return;
      }

      set({
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Something went wrong while loading Prime trial promo'
      });
    }
  },

  closeModal: () => {
    set({
      open: false,
      loading: false,
      error: null,
      onDismiss: undefined,
      onJoinPrime: undefined
    });
  },

  joinPrime: () => {
    const { skuUid, goalSlug, goalUID } = get();

    if (!skuUid) {
      return;
    }

    window.location.href = `/goal/${goalSlug}/${goalUID}/subscribe/${skuUid}`;
  }
}));
